import type { ContactHistoryEntry } from '../address-book-bff/types';
import type { DealStage } from '../pipelines/types';
import { TeamAssociateStatusType } from '../types';

export const GAMMA_TEAM_QUERY_KEYS = {
  dashboard: 'gamma-team-dashboard',
  myInvitations: 'gamma-team-my-invitations',
  activity: 'gamma-team-activity',
  memberActivity: 'gamma-team-member-activity',
  activityDay: 'gamma-team-activity-day',
  goals: 'gamma-team-goals',
};

export const gammaTeamRole = {
  Superadmin: 'GammaSuperAdmin',
  Admin: 'GammaAdmin',
  Approver: 'GammaApprover',
  Initiator: 'GammaInitiator',
  Viewer: 'GammaViewer',
} as const;
export type GammaTeamRole = (typeof gammaTeamRole)[keyof typeof gammaTeamRole];

export const gammaTeamRoleDisplayMap: Record<GammaTeamRole, string> = {
  GammaSuperAdmin: 'Superadmin',
  GammaAdmin: 'Admin',
  GammaApprover: 'Approver',
  GammaInitiator: 'Initiator',
  GammaViewer: 'Viewer',
};

export const selectableGammaTeamRoles: { label: string; value: GammaTeamRole }[] = [
  { label: 'Admin', value: gammaTeamRole.Admin },
  { label: 'Approver', value: gammaTeamRole.Approver },
  { label: 'Initiator', value: gammaTeamRole.Initiator },
  { label: 'Viewer', value: gammaTeamRole.Viewer },
];

export interface GammaTeamMemberRole {
  id: string;
  name: string;
  systemKey: string | null;
}

export interface GammaTeamMember {
  id: string;
  userId: string | null;
  invitationId: string | null;
  invitationStatus: UserInvitationStatus | null;
  email: string;
  name: string | null;
  role: GammaTeamRole;
  roles: GammaTeamMemberRole[];
  gammaJobTitle: string | null;
  headshotFileId: string | null;
  status: TeamAssociateStatusType;
  createdAt: string;
}

export interface GetGammaTeamDashboardRes {
  data: GammaTeamMember[];
  totalCount: number;
  page: number;
  limit: number;
  /** Members by role. Counts pending invitations too, so it over-reports actual holders. */
  roleStats: Record<string, number>;
  statusStats: Record<TeamAssociateStatusType, number>;
  /**
   * Registered Superadmins, invitations excluded — the count `RemoveFromGammaTeamHandler` applies
   * its last-Superadmin floor to, so it is the only one a UI guard may trust.
   */
  superAdminHolderCount: number;
}

export interface GetGammaTeamDashboardParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: TeamAssociateStatusType | '';
}

export interface CreateGammaTeamInvitationBody {
  email: string;
  role: GammaTeamRole;
  jobTitle: string;
}

export interface UpdateGammaTeamInvitationBody {
  role?: GammaTeamRole;
  jobTitle?: string | null;
}

export interface UpdateGammaTeamMemberBody {
  role?: GammaTeamRole;
  jobTitle?: string | null;
}

export const userInvitationStatus = {
  Created: 'Created',
  Accepted: 'Accepted',
  Rejected: 'Rejected',
  Completed: 'Completed',
} as const;
export type UserInvitationStatus = (typeof userInvitationStatus)[keyof typeof userInvitationStatus];

export interface MyGammaTeamInvitation {
  id: string;
  email: string;
  role: GammaTeamRole;
  jobTitle: string | null;
  status: UserInvitationStatus;
  createdAt: string;
  updatedAt: string;
}

export const activityPeriod = {
  ThisWeek: 'this-week',
  LastWeek: 'last-week',
  ThisMonth: 'this-month',
  LastMonth: 'last-month',
  Last30Days: 'last-30-days',
  ThisQuarter: 'this-quarter',
  Custom: 'custom',
} as const;
export type ActivityPeriod = (typeof activityPeriod)[keyof typeof activityPeriod];

export const ACTIVITY_ENTRY_KINDS = ['Call', 'Message', 'Meeting', 'Note'] as const;
export type ActivityEntryKind = (typeof ACTIVITY_ENTRY_KINDS)[number];

export interface GetActivityParams {
  period: ActivityPeriod;
  from?: string;
  to?: string;
  timeZone: string;
  memberIds?: string[];
}

export interface ActivityCounts {
  contactsReached: number;
  entries: number;
  calls: number;
  messages: number;
  meetings: number;
  notes: number;
  dealsCreated: number;
  dealsAdvanced: number;
}

export interface ActivityRange {
  from: string;
  to: string;
}

export interface ActivityMember {
  userId: string;
  name: string | null;
  email?: string | null;
  role: string | null;
  former: boolean;
}

export type TeamActivityMember = ActivityMember &
  ActivityCounts & {
    lastActiveAt?: string | null;
    lastActivityAt?: string | null;
    dailyActivity?: number[];
  };

export interface TeamActivityDay extends ActivityCounts {
  date: string;
  activeMembers: number;
}

export interface TeamActivityComparison {
  range: ActivityRange;
  total: ActivityCounts;
  activeMembers?: number;
  activeDays?: number;
}

export interface TeamActivityResponse {
  range: ActivityRange;
  members: TeamActivityMember[];
  total: ActivityCounts;
  previous?: TeamActivityComparison;
  daily?: TeamActivityDay[];
}

export interface ActivityContact {
  id: string;
  name: string | null;
  referenceType: string | null;
  referenceId: string | null;
  deleted: boolean;
}

export interface MemberActivityContact {
  contact: ActivityContact;
  entries: number;
  kinds: ActivityEntryKind[];
  lastEntryAt: string;
  items: ContactHistoryEntry[];
}

export interface MemberActivityResponse {
  range: ActivityRange;
  member: ActivityMember;
  summary: ActivityCounts;
  contacts: MemberActivityContact[];
  truncated: boolean;
  deals: MemberActivityDeal[];
}

export interface GetActivityDayParams {
  date: string;
  timeZone: string;
  memberIds?: string[];
}

export interface ActivityDayEntry {
  actor: ActivityMember;
  contact: ActivityContact;
  entry: ContactHistoryEntry;
}

export type ActivityDayDeal = MemberActivityDeal & { actor: ActivityMember };

export interface ActivityDayResponse {
  date: string;
  range: ActivityRange;
  entries: ActivityDayEntry[];
  deals: ActivityDayDeal[];
  truncated: boolean;
}

export const TEAM_GOAL_METRICS = [
  'contactsReached',
  'entries',
  'calls',
  'messages',
  'meetings',
  'notes',
  'dealsCreated',
  'dealsAdvanced',
] as const satisfies readonly (keyof ActivityCounts)[];
export type TeamGoalMetric = (typeof TEAM_GOAL_METRICS)[number];

export const teamGoalCadence = { Weekly: 'weekly', Monthly: 'monthly' } as const;
export type TeamGoalCadence = (typeof teamGoalCadence)[keyof typeof teamGoalCadence];

export interface TeamGoalInput {
  metric: TeamGoalMetric;
  cadence: TeamGoalCadence;
  target: number;
}

export interface TeamGoal extends TeamGoalInput {
  userId: string;
  updatedAt: string;
}

export interface TeamGoalsResponse {
  goals: TeamGoal[];
}

export interface MemberActivityDeal {
  entryId: string;
  deleted: boolean;
  companyName: string | null;
  buyer: { id: string; name: string | null };
  stage: DealStage;
  createdAt: string | null;
  advancedAt: string | null;
}
